import { Component, computed, input, Signal } from '@angular/core';
import moment from 'moment';
import { GridBaseComponent } from 'src/app/OFramework/Grid/GridBaseComponent';
import { ViewConfigGRPCService } from 'src/app/ProxyServices/view-config.grpc.service';
import { WorkorderGRPCService } from 'src/app/ProxyServices/workorder.grpc.service';
import { UserGRPCService } from 'src/app/ProxyServices/user.grpc.service';
import { ColumnSettings, ViewConfigSave } from '../../../CommonFiles/UIModels';
import { State } from '@progress/kendo-data-query';
import * as XLSX from 'xlsx';
import { ShowItem } from '../../home.component';
import {
  ViewResponseModel,
  WorkOrderBase,
} from '../../../CommonFiles/BusinessModels';
import {
  faChevronsRight,
  faFilter,
  faDownload,
  faBookmark,
  faAngleDown,
  faTimes,
  faFileXls,
  faFileCsv,
  faFilePdf,
  faFile,
  faSpinner,
  faCircleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { OAuthService } from '@olameter/o-authentication';
import { toSignal } from '@angular/core/rxjs-interop';
import { ZonedDate } from '@progress/kendo-date-math';

@Component({
  standalone: false,
  selector: 'app-actions-ribbon',
  templateUrl: './actions-ribbon.component.html',
  styleUrls: ['./actions-ribbon.component.scss'],
})
export class ActionsRibbonComponent {
  excelPending: boolean = false;
  faChevronsRight = faChevronsRight;
  faFilter = faFilter;
  faDownload = faDownload;
  faBookmark = faBookmark;
  faAngleDown = faAngleDown;
  faTimes = faTimes;
  faFileXls = faFileXls;
  faFileCsv = faFileCsv;
  faFilePdf = faFilePdf;
  faFile = faFile;
  faSpinner = faSpinner;
  faCircleExclamation = faCircleExclamation;

  showItem = input(ShowItem.All);
  workOrders = input<WorkOrderBase[]>([]);
  selectedWorkOrders = input<WorkOrderBase[]>([]);
  columnsList = input<ColumnSettings[]>([]);
  gridState = input<State>();
  timezone = input<string>();

  showAddViewDialog: boolean = false;
  isNew: boolean = false;

  // cols computed values
  selectedWOstatus = computed<string[]>(() => {
    return this.selectedWorkOrders().map((x) => x.StatusWeb);
  });

  createViewModel = computed<ViewConfigSave | undefined>(() => {
    const active = this.active();
    if (active) {
      return {
        viewConfigTitle: active.title,
        keepFilters: true, //default value in the view to show
      };
    } else {
      return undefined;
    }
  });
  active!: Signal<ViewResponseModel>;

  constructor(
    public authService: OAuthService,
    public _clientUser: UserGRPCService,
    public _clientViewConf: ViewConfigGRPCService,
    public _clientWO: WorkorderGRPCService
  ) {
    this.active = toSignal(this._clientViewConf.getActiveView());
  }

  onCloseReloadGrid(updatedWorkOrders) {
    // update work orders
    let workOrders = [...this._clientWO.getValue()];

    // updatedWorkOrdersMap map for quick look up.
    const updatedWorkOrdersMap = new Map<string, any>();
    for (const wo of updatedWorkOrders) {
      updatedWorkOrdersMap.set(wo.name, wo);
    }
    for (let i = 0; i < workOrders.length; i++) {
      const wo = workOrders[i];
      if (updatedWorkOrdersMap.has(wo.name)) {
        const newWO = updatedWorkOrdersMap.get(wo.name);
        workOrders[i] = {
          ...wo,
          StatusWeb: newWO.StatusWeb,
          AssignedTo: newWO.AssignedTo,
          FsrIdentifier: newWO.FsrIdentifier,
          InstallDate: newWO.InstallDate ? newWO.InstallDate :  workOrders[i].InstallDate,
        };
      }
    }

    // filter work orders
    const currentView = this._clientViewConf.getCurrentActiveView();
    const woStatusValues = currentView.WoStatus.value;
    workOrders = workOrders.filter((user) =>
      woStatusValues.includes(user.StatusWeb)
    );
    // update the work orders list
    this._clientWO.next(workOrders);
    // reset selected work orders
    this._clientWO.RXselectedWOIDsShared.next([]);
  }

  exportIFSFile() {
    var currentView = this._clientViewConf.getCurrentActiveView();
    this._clientWO
      .exportIFSFile(
        {
          contractName: currentView?.contractName,
          startDate: currentView?.startDateFilter,
          endDate: currentView?.endDateFilter,
        },
        GridBaseComponent.initialState, //always pass an empty filter , as the IFS export should not be impacted by Filters
        this._clientViewConf.columnsConfCurrentigTobeRX,
        currentView?.installDateOption
      )
      .subscribe((value: any) => {
        let file = new Blob([value], { type: 'text/csv' });
        let a = document.createElement('a');
        var url = URL.createObjectURL(file);
        a.href = url;
        a.download =
          currentView.contractTitle +
          '_' +
          moment(currentView.startDateFilter).format('MMMDDYYYY') +
          '_' +
          moment(new Date()).format('YYYYMMDDHmmss');
        document.body.appendChild(a);

        a.click();
        setTimeout(function () {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 0);
      });
  }

  export(type): void {
    if (this.excelPending === true) {
      return;
    }

    this.excelPending = true;
    const fileName = 'wo.' + type;
    const transformDate = (date) => {
      if (date != null && this.timezone() != null && date instanceof Date) {
        var ad = ZonedDate.fromUTCDate(date);
        return ad.toTimezone(this.timezone()).toUTCDate();
      } 
      return date;
    }

    let rowData: WorkOrderBase[] = this.workOrders().map((wo) => {
        return {
          ...wo,
          EventDate: transformDate(wo['EventDateWithTime']),
          InstallDate: transformDate(wo['InstallDateWithTime']),
          AppointmentDate: transformDate(wo['AppointmentDateWithTime']),
          CompletedOn: transformDate(wo['CompletedOnWithTime']),
        };
      });
    if (this.showItem() === ShowItem.Selected) {
      const selectedSet = new Set(
        this.selectedWorkOrders().map((feature) => feature.name)
      );
      rowData = rowData.filter((wo) => selectedSet.has(wo.name));
    }
    if (this.showItem() === ShowItem.UnSelected) {
      const selectedSet = new Set(
        this.selectedWorkOrders().map((feature) => feature.name)
      );
      rowData = rowData.filter((wo) => !selectedSet.has(wo.name));
    }
    const columnDefs = this.columnsList();
    const filter = this.gridState().filter;

    if (typeof Worker !== 'undefined') {
      const worker = new Worker(
        new URL('../../../excel-export.worker', import.meta.url),
        { type: 'module' }
      );
      worker.postMessage({
        rowData,
        columnDefs,
        filter,
      });

      worker.onmessage = (event) => {
        const blob = event.data;
        this.exportFile(fileName, blob);

        worker.terminate();
      };

      worker.onerror = (error) => {
        console.error('Worker error:', error);
        this.excelPending = false;
        worker.terminate();
      };
    } else {
      const filteredData = rowData.filter((item) =>
        this.evaluateFilters(item, filter)
      );
      // Transform the data into a format suitable for SheetJS
      const headers = columnDefs
        .filter((item) => item.hidden === false)
        .map((col) => col.field);
      const rows = filteredData.map((row) =>
        headers.map((header) => row[header])
      );
      if (type === 'csv') {
        // Convert filtered data to a worksheet
        const worksheet = XLSX.utils.json_to_sheet(rows);

        // Convert the worksheet to CSV
        const csv = XLSX.utils.sheet_to_csv(worksheet);

        // Create a Blob from the CSV data
        const csvBlob = new Blob([csv], { type: 'text/csv' });

        this.exportFile(fileName, csvBlob);
      } else {
        // Create a worksheet and workbook
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

        // Generate the Excel file as a Blob
        const excelBlob = XLSX.write(workbook, {
          bookType: 'xlsx',
          type: 'array',
        });

        // Convert workbook to a Blob
        const workbookBlob = new Blob([excelBlob], {
          type: 'application/octet-stream',
        });
        this.exportFile(fileName, workbookBlob);
      }
    }
  }

  // Helper function to evaluate a single filter
  evaluateFilter(item: any, filter: any): boolean {
    const { field, operator, value } = filter;
    const itemValue = item[field];

    // Check for null or undefined values
    if (itemValue === null || itemValue === undefined) {
      return operator === 'isnull';
    }

    const itemValueStr = itemValue.toString();

    switch (operator) {
      case 'eq':
        return itemValue === value;
      case 'neq':
        return itemValue !== value;
      case 'gt':
        return itemValue > value;
      case 'lt':
        return itemValue < value;
      case 'gte':
        return itemValue >= value;
      case 'lte':
        return itemValue <= value;
      case 'contains':
        return itemValueStr.toLowerCase().includes(value.toLowerCase());
      case 'doesnotcontain':
        return !itemValueStr.toLowerCase().includes(value.toLowerCase());
      case 'startsWith':
        return itemValueStr.toLowerCase().startsWith(value.toLowerCase());
      case 'endsWith':
        return itemValueStr.toLowerCase().endsWith(value.toLowerCase());
      case 'isnull':
        return itemValue === null || itemValue === undefined;
      default:
        return true;
    }
  }

  // Recursive function to evaluate a Kendo filter object
  evaluateFilters(item: any, filtersObj: any): boolean {
    const { filters, logic } = filtersObj;

    if (logic === 'and') {
      return filters.every((filter) => {
        if (filter.filters) {
          return this.evaluateFilters(item, filter);
        } else {
          return this.evaluateFilter(item, filter);
        }
      });
    } else if (logic === 'or') {
      return filters.some((filter) => {
        if (filter.filters) {
          return this.evaluateFilters(item, filter);
        } else {
          return this.evaluateFilter(item, filter);
        }
      });
    }
    return true;
  }

  // export file using blob
  exportFile(fileName, blob) {
    // Send the Blob back to the main thread
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    this.excelPending = false;
  }

  save(payload) {
    const isNew = this.isNew;
    const user = this._clientUser.getValue()[0];
    this._clientViewConf.saveViewConfigHandler(
      payload.keepFilters,
      payload.viewConfigTitle,
      user,
      isNew
    );
    this.toggleAddViewDialog();
  }
  toggleAddViewDialog(isNew?: boolean) {
    this.isNew = isNew;
    this.showAddViewDialog = !this.showAddViewDialog;
  }
}
